// web_components/wcCase/wcCase_Utils.ts

// import { applyFocusTrap, clearFocusTrap } from '../../utils/focusTrap';
import { dispatchAddToBasket } from '../../events/addToBasketEvent';

/**
 * Matches predefined patterns like 'White Wine Case', 'Mixed Wine Case', and 'Red Wine Case'
 * within the product title and wraps them in a <span> element.
 *
 * @param {string} title - The product title to search for matching patterns.
 * @returns {string} - The product title with matched patterns wrapped in <span> elements.
 */
export function wrapCasePattern(title: string): string {
	// Define the patterns you want to match
	const patterns = ['White Wine Case', 'Mixed Wine Case', 'Red Wine Case', 'in Wooden Gift Box'];

	// Loop through each pattern and replace occurrences with a <span> wrapper
	patterns.forEach((pattern) => {
		const regex = new RegExp(`(${pattern})`, 'g'); // Create a regex to match the pattern globally
		title = title.replace(regex, '<span>$1</span>'); // Wrap the matched pattern in <span>
	});

	return title; // Return the updated title
}

/**
 * Calculates pricing details for a given product, including current price, original price,
 * discount percentage, and preorder deposit (if applicable).
 *
 * - If `wasPrice` is greater than `nowPrice`, a rounded discount percentage is returned.
 * - If `wasPrice` is less than or equal to `nowPrice`, both `wasPrice` and `discount` are set to 0.
 * - If `preorder_deposit` is provided, it is returned as a string formatted to two decimal places (e.g. "99.00").
 * - If `preorder_deposit` is empty or not present, an empty string is returned.
 *
 * @param {object} productData - The product data object.
 * @returns {{
 *   nowPrice: number,
 *   wasPrice: number,
 *   discount: number,
 *   preorderDeposit: string
 * }} An object with the current price, original price (if discounted), discount percentage, and preorder deposit.
 */
export function getPricingDetails(productData: any): {
	nowPrice: number;
	wasPrice: number;
	discount: number;
	preorderDeposit: string;
} {
	const nowPrice = parseFloat(productData.price) || 0;
	let wasPrice = parseFloat(productData.was_price) || 0;
	let discount = 0;

	let preorderDeposit = '';

	if (productData.preorder_deposit !== '' && productData.preorder_deposit != null) {
		const raw = parseFloat(productData.preorder_deposit);
		if (!isNaN(raw)) {
			preorderDeposit = raw.toFixed(2);
		}
	}

	if (wasPrice > 0 && nowPrice < wasPrice) {
		discount = Math.floor(((wasPrice - nowPrice) / wasPrice) * 100);
	} else {
		wasPrice = 0;
		discount = 0;
	}

	return { nowPrice, wasPrice, discount, preorderDeposit };
}

/**
 * Determines the stock status of the product, including preorder, out-of-stock, and sold-out states.
 * Returns an object containing both the stock status CSS class and the corresponding stock status message.
 *
 * @param {object} productData - The product data.
 * @returns {{stockStatusClass: string, stockStatusMessage: string}} -
 * The stock status information including CSS class and the display message.
 */
export function getStockStatus(productData: any): {
	stockStatusClass: string;
	stockStatusMessage: string;
} {
	// Direct return using a ternary operator for concise logic
	return productData.availability_state === 'preorder'
		? { stockStatusClass: 'wc-case__pre-sell-item', stockStatusMessage: 'Reserve now' }
		: productData.store_stock_level <= 0
			? { stockStatusClass: 'wc-case__out-of-stock', stockStatusMessage: 'Out of stock' }
			: productData.force_sold_out === 'true'
				? { stockStatusClass: 'wc-case__sold-out', stockStatusMessage: 'Sold out' }
				: { stockStatusClass: '', stockStatusMessage: '' };
}

/**
 * Determines the product type and returns both a CSS class and the appropriate image URL.
 * Handles different product types, such as pre-mixed wine cases, single bottles, and advent calendars.
 * Mantiki variables can be used for future logic.
 *
 * @param {object} productData - The product data.
 * @returns {{ productTypeClass: string, productImage: string }} - The product type class and the product image URL.
 */
export function getProductTypeDetails(productData: any): {
	productType: string;
	productTypeClass: string;
	productImage: string;
	productUrl: string;
} {
	let productType = '';
	let productTypeClass = '';
	let productImage = '';
	let productUrl = '';
	let productUrlSegment = '';

	// Helper function to format the size as a three-digit number (e.g., 001, 200, 720)
	const formatSize = (size: number): string => {
		return size.toString().padStart(3, '0');
	};

	// Process the product image by replacing '/000' and changing the file extension
	const processProductImage = (imageUrl: string, size: number): string => {
		const formattedSize = formatSize(size);
		// Replace '/000' with the formatted size and change the file extension
		return imageUrl.replace('/000', `/${formattedSize}`).replace(/\.\w+$/, ``);
	};

	// Switch case for determining the product type class, corresponding image size, and file type
	switch (productData.product_type_name) {
		case 'pre_mixed_wine_cases':
			productUrlSegment = 'wine-case';
			productImage = processProductImage(productData.product_images, 720);
			break;
		case 'wine_bottles':
			productUrlSegment = 'wine';
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'beer_bottles':
			productUrlSegment = 'beer';
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'spirits':
			productUrlSegment = 'spirit';
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'advent_calendar':
			productUrlSegment = 'gifts/' + productType;
			productImage = processProductImage(productData.product_images, 720);
			break;
		case 'event_tickets':
			productUrlSegment = 'event';
			productImage = processProductImage(productData.product_images, 720);
			break;
		default:
			productUrlSegment = productType;
			productImage = processProductImage(productData.product_images, 720);
	}

	productType = productData.product_type_name;
	productTypeClass = 'wc-product__type-' + productType;
	productUrl = window.serverData.contextPath + '/' + productUrlSegment + '/' + productData.url;

	return { productType, productTypeClass, productImage, productUrl };
}

/**
 * Handles the form submission by processing the product type, quantity, and selected option.
 *
 * This method is called when the form is submitted. It retrieves the product type,
 * quantity, and selected option from the form, logs them to the console, and allows
 * further actions like sending the data via an AJAX request or updating the UI.
 *
 * @param {HTMLElement} targetElement - The product.
 * @param {string} productId - The id of the product.
 * @param {string} productType - The type of product being added to the cart (e.g., default, Mixed).
 * @param {string} quantity - The quantity of the product to be added to the cart.
 * @param {boolean} isPersonalized - Is this a personalised product?
 * @returns {void} This function does not return anything, but it handles form submission logic.
 */
export function handleFormSubmission(
	targetElement: HTMLElement,
	productId: number,
	productType: string,
	quantity: string,
	isPersonalized: boolean,
): void {
	isPersonalized = false;

	dispatchAddToBasket({
		targetElement,
		productId,
		productType,
		quantity,
		isPersonalized,
	});
}
